import { FixtureResponse } from '../../services/fixtureClient';

/**
 * Envelope builders for fixtures. Every fixture returns the full
 * `ApiResponseRaw` shape — `useApiClientStore.onError` reads `response.errors`
 * and branches on `errors[0].redirectToError`, so payload-only fixtures would
 * leave that path untested.
 */

export function ok<T>(data: T, status = 200): FixtureResponse<T> {
  return { ok: true, status, data };
}

/** Field-keyed violations, as `PatientPortalController::formatCommandErrors` returns them. */
export function validationError(
  errors: Record<string, string[]>,
  status = 400,
): FixtureResponse<null> {
  return { ok: false, status, data: null, errors };
}

/**
 * A domain failure carrying a code from `errors.data.ts`. `fields` are the siblings the
 * endpoint sends beside the code (the lockout's `lockedUntil`).
 *
 * ENG-21798: no `redirectToError` — the API never sends it, and a fixture asserting it
 * hid the fact that the catalogue's own codes were wrong.
 */
export function domainError(
  code: string,
  { status = 400, message, fields }: DomainErrorOptions = {},
): FixtureResponse<null> {
  return {
    ok: false,
    status,
    data: null,
    errors: [{ code, message, ...fields }],
  };
}

type DomainErrorOptions = {
  status?: number;
  message?: string;
  fields?: Record<string, unknown>;
};

export function sessionExpired(): FixtureResponse<null> {
  return domainError('user-session-invalid', { status: 401 });
}

export function unprocessable(code = 'unprocessable-entity'): FixtureResponse<null> {
  return domainError(code, { status: 422 });
}

export function serverError(): FixtureResponse<null> {
  return { ok: false, status: 500, data: null, text: 'Internal Server Error' };
}
